import { useState, useEffect, memo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import { useRoleSwitcher } from '../hooks/useRoleSwitcher.jsx';
import { useSchool } from '../../../contexts/SchoolContext';
import { userService } from '../../../services/UserService';
import { Card, Alert, LoadingSpinner } from '../../../components/shared/UIComponents';
import { AnimatedToggle } from '../../../components/shared/AnimatedToggle';
import {
  UserIcon,
  ShieldCheckIcon,
  CogIcon
} from '@heroicons/react/24/outline';

// Memoized user row component to prevent unnecessary re-renders
const UserRow = memo(({
  userData,
  isCurrentUser,
  onUpdateUserRole,
  schoolId
}) => {
  // Derive display values from school-scoped role (with legacy fallback)
  const accessRole = userData.schoolRoles?.[schoolId]?.accessRole
    ?? (userData.admin ? 'admin' : userData.staff ? 'staff' : null);
  const isAdmin = accessRole === 'admin';
  const isStaff = accessRole === 'staff' || accessRole === 'admin';

  return (
    <tr className={isCurrentUser ? 'bg-base-200/50' : ''}>
      <td>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center text-sm font-semibold">
            {(userData.displayName || userData.email)?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex flex-col">
            <span className="font-medium">
              {userData.displayName || 'Unknown User'}
              {isCurrentUser && <span className="text-xs text-primary ml-2">(You)</span>}
            </span>
          </div>
        </div>
      </td>
      <td className="text-sm text-base-content/70">
        {userData.email}
      </td>
      <td className="text-center">
        <AnimatedToggle
          checked={isAdmin}
          onChange={() => onUpdateUserRole(userData.id, { accessRole: !isAdmin ? 'admin' : 'staff' }, schoolId)}
          disabled={isCurrentUser}
          title={isCurrentUser
            ? 'Cannot edit your own permissions'
            : isAdmin
              ? 'Demote to staff'
              : 'Promote to admin'
          }
        />
      </td>
      <td className="text-center">
        <AnimatedToggle
          checked={userData.dev || false}
          onChange={() => onUpdateUserRole(userData.id, 'dev', !userData.dev)}
          disabled={isCurrentUser}
          title={isCurrentUser
            ? 'Cannot edit your own permissions'
            : userData.dev
              ? 'Revoke developer access'
              : 'Grant developer access'
          }
        />
      </td>
      <td className="text-center">
        <AnimatedToggle
          checked={isStaff}
          onChange={() => onUpdateUserRole(userData.id, { accessRole: !isStaff ? 'staff' : null }, schoolId)}
          disabled={isCurrentUser}
          title={isCurrentUser
            ? 'Cannot edit your own permissions'
            : isStaff
              ? 'Revoke staff access'
              : 'Grant staff access'
          }
        />
      </td>
      <td className="text-sm text-base-content/70">
        {userData.createdAt
          ? new Date(userData.createdAt.seconds * 1000).toLocaleDateString()
          : 'Unknown'
        }
      </td>
    </tr>
  );
});

UserRow.displayName = 'UserRow';

const RoleManagement = ({ embedded = false } = {}) => {
  const { user } = useAuth();
  const { currentPermissions } = useRoleSwitcher();
  const { schoolId } = useSchool();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await userService.getAllUsers(true, schoolId);
      setUsers(allUsers);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = useCallback(async (userId, roleUpdate, targetSchoolId) => {
    try {
      // CRITICAL: Prevent self-demotion
      if (userId === user?.uid) {
        setMessage({
          type: 'error',
          text: 'Cannot modify your own permissions to prevent lockout!'
        });
        return;
      }

      // Handle dev flag (legacy global, not school-scoped)
      if (typeof roleUpdate === 'string') {
        // Legacy path: (userId, 'dev', value) — dev flag stays global
        const roleType = roleUpdate;
        const value = targetSchoolId; // third arg is value in legacy call
        await userService.updateUserRole(userId, { [roleType]: value }, user?.uid);
        setUsers(prevUsers =>
          prevUsers.map(u =>
            u.id === userId ? { ...u, [roleType]: value } : u
          )
        );
        setMessage({
          type: value ? 'success' : 'info',
          text: `User ${roleType} permission ${value ? 'granted' : 'revoked'}`
        });
        return;
      }

      // School-scoped role update: roleUpdate = { accessRole: '...' }
      const { accessRole } = roleUpdate;

      // Prevent removing the last admin
      if (accessRole !== 'admin') {
        const adminCount = users.filter(u => {
          const ar = u.schoolRoles?.[targetSchoolId]?.accessRole ?? (u.admin ? 'admin' : null);
          return ar === 'admin' && u.id !== userId;
        }).length;
        if (adminCount === 0) {
          setMessage({
            type: 'error',
            text: 'Cannot remove the last administrator!'
          });
          return;
        }
      }

      // Update school-scoped role via userService (4-arg signature)
      await userService.updateUserRole(userId, roleUpdate, user?.uid, targetSchoolId);

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u => {
          if (u.id !== userId) return u;
          const updatedSchoolRoles = {
            ...(u.schoolRoles || {}),
            [targetSchoolId]: {
              ...(u.schoolRoles?.[targetSchoolId] || {}),
              accessRole
            }
          };
          return {
            ...u,
            schoolRoles: updatedSchoolRoles,
            admin: accessRole === 'admin',
            staff: accessRole === 'staff' || accessRole === 'admin'
          };
        })
      );

      setMessage({
        type: 'success',
        text: `User role updated to ${accessRole ?? 'none'}`
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update user permissions' });
    }
  }, [user?.uid, users, schoolId]);

  // Check if current user can manage roles
  if (!currentPermissions?.canManageUsers) {
    return (
      <div className="space-y-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold">Role Management</h1>
            <p className="text-base-content opacity-70">Manage user permissions and roles</p>
          </div>
        )}
        <Alert
          type="warning"
          message="You don't have permission to manage user roles."
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl font-bold">Role Management</h1>
            <p className="text-base-content opacity-70">Manage user permissions and roles</p>
          </div>
        )}
        <Card>
          <LoadingSpinner size="large" />
          <p className="text-center mt-4">Loading users...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 transition-all duration-300 ease-out">
      {!embedded && (
        <div>
          <h1 className="text-3xl font-bold">Role Management</h1>
          <p className="text-base-content opacity-70">Manage user permissions and roles</p>
        </div>
      )}

      {message && (
        <Alert 
          type={message.type} 
          message={message.text} 
          onClose={() => setMessage(null)}
        />
      )}

      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <UserIcon className="w-5 h-5" />
          <h2 className="text-xl font-semibold">User Permissions</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th className="text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <ShieldCheckIcon className="w-4 h-4" />
                    <span>Admin</span>
                  </div>
                </th>
                <th className="text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <CogIcon className="w-4 h-4" />
                    <span>Developer</span>
                  </div>
                </th>
                <th className="text-center">Staff</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((userData) => (
                <UserRow
                  key={userData.id}
                  userData={userData}
                  isCurrentUser={userData.id === user?.uid}
                  onUpdateUserRole={updateUserRole}
                  schoolId={schoolId}
                />
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className="text-center py-8">
            <UserIcon className="w-12 h-12 mx-auto text-base-content/30 mb-2" />
            <p className="text-base-content/70">No other users found</p>
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold mb-2">Permission Levels</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-success/10 border border-success/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <ShieldCheckIcon className="w-5 h-5 text-success" />
              <h4 className="font-semibold text-success">Administrator</h4>
            </div>
            <p className="text-sm text-base-content/70">
              Full access to all features including user management and system settings.
            </p>
          </div>
          
          <div className="p-4 bg-info/10 border border-info/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <CogIcon className="w-5 h-5 text-info" />
              <h4 className="font-semibold text-info">Developer</h4>
            </div>
            <p className="text-sm text-base-content/70">
              Access to development tools, role switching, and advanced settings.
            </p>
          </div>
          
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <UserIcon className="w-5 h-5 text-warning" />
              <h4 className="font-semibold text-warning">Staff</h4>
            </div>
            <p className="text-sm text-base-content/70">
              Standard access to teaching tools and classroom management.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RoleManagement;